import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { partnerSafeguardingApi } from '../../api/partner-financial-safeguarding-policies';
import { ApiError } from '../../api/client';
import { usePermission } from '../../auth/AuthContext';
import { useToast } from '../../components/Toast';

interface FormValues {
  hasPolicy: boolean; hasFocalPoint: boolean; focalPointName: string; hasReportingMechanism: boolean; hasTraining: boolean;
  hasCodeOfConduct: boolean; hasRecruitmentScreening: boolean; hasComplaintsMechanism: boolean; hasInvestigationProcedure: boolean;
  isSurvivorCentred: boolean; coversChildSafeguarding: boolean; coversGbv: boolean; coversSexualHarassment: boolean; coversExploitationAbuse: boolean; notes: string;
}

/** Restricted (spec section 16) - genuinely hidden client-side for anyone without safeguarding.view, on top of real server-side enforcement. */
export function PartnerSafeguardingTab({ partnerId }: { partnerId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission('partner.due_diligence.safeguarding.manage');
  const dataQuery = useQuery({ queryKey: ['partner-safeguarding', partnerId], queryFn: () => partnerSafeguardingApi.get(partnerId) });
  const { register, handleSubmit, reset } = useForm<FormValues>();

  useEffect(() => {
    if (dataQuery.data?.data) {
      const d = dataQuery.data.data;
      reset({
        hasPolicy: !!d.has_policy, hasFocalPoint: !!d.has_focal_point, focalPointName: d.focal_point_name ?? '', hasReportingMechanism: !!d.has_reporting_mechanism,
        hasTraining: !!d.has_training, hasCodeOfConduct: !!d.has_code_of_conduct, hasRecruitmentScreening: !!d.has_recruitment_screening,
        hasComplaintsMechanism: !!d.has_complaints_mechanism, hasInvestigationProcedure: !!d.has_investigation_procedure, isSurvivorCentred: !!d.is_survivor_centred,
        coversChildSafeguarding: !!d.covers_child_safeguarding, coversGbv: !!d.covers_gbv, coversSexualHarassment: !!d.covers_sexual_harassment,
        coversExploitationAbuse: !!d.covers_exploitation_abuse, notes: d.notes ?? '',
      });
    }
  }, [dataQuery.data, reset]);

  const saveMutation = useMutation({
    mutationFn: (values: FormValues) => partnerSafeguardingApi.upsert(partnerId, values),
    onSuccess: () => { showSuccess('Safeguarding assessment saved.'); queryClient.invalidateQueries({ queryKey: ['partner-safeguarding', partnerId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save.'),
  });

  return (
    <div className="panel">
      <div className="panel-body">
        <form className="inline-form" onSubmit={handleSubmit((values) => saveMutation.mutate(values))} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
          <label><input type="checkbox" {...register('hasPolicy')} disabled={!canManage} /> Has Safeguarding Policy</label>
          <label><input type="checkbox" {...register('hasFocalPoint')} disabled={!canManage} /> Has Focal Point</label>
          <label>Focal Point Name<input {...register('focalPointName')} disabled={!canManage} /></label>
          <label><input type="checkbox" {...register('hasReportingMechanism')} disabled={!canManage} /> Has Reporting Mechanism</label>
          <label><input type="checkbox" {...register('hasTraining')} disabled={!canManage} /> Has Training</label>
          <label><input type="checkbox" {...register('hasCodeOfConduct')} disabled={!canManage} /> Has Code of Conduct</label>
          <label><input type="checkbox" {...register('hasRecruitmentScreening')} disabled={!canManage} /> Has Recruitment Screening</label>
          <label><input type="checkbox" {...register('hasComplaintsMechanism')} disabled={!canManage} /> Has Complaints Mechanism</label>
          <label><input type="checkbox" {...register('hasInvestigationProcedure')} disabled={!canManage} /> Has Investigation Procedure</label>
          <label><input type="checkbox" {...register('isSurvivorCentred')} disabled={!canManage} /> Is Survivor-Centred</label>
          <label><input type="checkbox" {...register('coversChildSafeguarding')} disabled={!canManage} /> Covers Child Safeguarding</label>
          <label><input type="checkbox" {...register('coversGbv')} disabled={!canManage} /> Covers GBV</label>
          <label><input type="checkbox" {...register('coversSexualHarassment')} disabled={!canManage} /> Covers Sexual Harassment</label>
          <label><input type="checkbox" {...register('coversExploitationAbuse')} disabled={!canManage} /> Covers Exploitation/Abuse</label>
          <label style={{ width: '100%' }}>Notes<textarea {...register('notes')} disabled={!canManage} /></label>
          {canManage && <button type="submit" disabled={saveMutation.isPending}>{saveMutation.isPending ? 'Saving…' : 'Save Assessment'}</button>}
        </form>
      </div>
    </div>
  );
}
